import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks';
import { $api } from '@service/RequestService';
import { Edge } from '@xyflow/react';
import { useMutation } from 'react-query';

import { flowChatData } from '../constants';
import { generateRequestBodyFromNodes } from '../helpers';
import { IFlowNode } from '../models';
import { setEdges, setNodes } from '../store';

type CustomEdge = Edge & { option?: number };

interface NewEdgeResult {
  id?: number;
  source: string;
  target: string;
  sourceHandle?: string;
  option?: number;
}

export function useCreateChatFlowBlock(resetChanges: () => void) {
  const dispatch = useAppDispatch();
  const { edges } = useAppSelector(state => state.aiAgentReducer);

  const body = generateRequestBodyFromNodes();

  return useMutation({
    mutationFn: async (chatFlowId: number) => {
      const res = await $api.post(`/v1/chat-flow/block/${chatFlowId}`, body);

      return res.data;
    },
    onSuccess: res => {
      const newNodes: IFlowNode[] = (res?.data as any[])?.map(item => ({
        id: String(item?.id),
        oldId: item?.oldId,
        position: {
          x: item?.x,
          y: item?.y
        },
        data: {
          name: item?.name,
          description: item?.description,
          ...(item?.type === 'QUESTION_CLASSIFIER' && { options: item?.options }),
          json: {
            ...JSON.parse(item?.dataJson),
            ...(item?.inputType && { inputType: item?.inputType }),
            ...(item?.tokenLimit && { tokenLimit: item?.tokenLimit }),
            ...(item?.ai && { ai: item?.ai }),
            ...(item?.useOldConversation && { useOldConversation: item?.useOldConversation })
          }
        },
        type: item?.type,
        measured: {
          width: flowChatData[item?.type as keyof typeof flowChatData]?.width,
          height: flowChatData[item?.type as keyof typeof flowChatData]?.height
        },
        ...(item?.type === 'QUESTION_CLASSIFIER' && { options: item?.options })
      }));

      const newEdges = (edges as CustomEdge[])
        ?.map(item => {
          const sourceNode = newNodes?.find(node => node.oldId === item.source);
          const targetNode = newNodes?.find(node => node.oldId === item.target);

          if (!sourceNode || !targetNode) return null;

          const result: NewEdgeResult = {
            source: String(sourceNode.id),
            target: String(targetNode.id)
          };

          if (typeof item.id === 'number') {
            result.id = item.id;
          }

          if (sourceNode.type === 'QUESTION_CLASSIFIER') {
            const options = sourceNode.options || (sourceNode.data as any)?.options;

            if (Array.isArray(options)) {
              const optionIndex = Number(item?.sourceHandle?.replace('source_', ''));

              if (optionIndex !== -1) {
                result.sourceHandle = `source_${optionIndex}`;
                result.option = item.option || options[optionIndex]?.id;
              }
            }
          }

          return result;
        })
        .filter(Boolean);

      dispatch(setEdges(() => newEdges as any));
      dispatch(setNodes(() => newNodes as any));
      resetChanges();
    }
  });
}
